import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

/* ============================================================
 * KEYFRAMES
 * ============================================================ */
const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideInRight = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const slideOutRight = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50%      { opacity: 0.85; }
`;

const spinKf = keyframes`
  to { transform: rotate(360deg); }
`;

/* ============================================================
 * MIXINS
 * ============================================================ */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * CONTAINER (root)
 * ============================================================ */
export const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  .spin { animation: ${spinKf} 1s linear infinite; }

  .content-wrapper {
    width: 100%;
    max-width: 90em;
    margin: 0 auto;
    padding: 0 2em;
    display: flex;
    flex-direction: column;
    gap: 14px;
    height: 100%;
    min-height: 0;
    overflow: hidden;

    @media (max-width: 768px) {
      padding: 0 1em;
    }
  }

  /* ============================================================
   * HEADER
   * ============================================================ */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 4px;
    flex-shrink: 0;

    .header-icon { display: none; }

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "LEAD SCRAPER · PUPPETEER";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          background: transparent !important;
          padding: 0 !important;
          opacity: 1 !important;

          .dot {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }

          &.active {
            color: ${tokens.brand};

            &::before {
              content: none;
            }
          }

          &.offline {
            color: ${tokens.danger};

            &::before {
              content: "";
              width: 7px;
              height: 7px;
              border-radius: 50%;
              background: ${tokens.danger};
              opacity: 0.7;
            }
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;
          background: transparent !important;
          padding: 0 !important;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }

    .toolbar-right {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-shrink: 0;
    }

    .btn-action {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: transparent;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.85;
      font-family: ${tokens.fontSans};
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      letter-spacing: -0.005em;
      white-space: nowrap;

      svg { font-size: 0.95em; }

      &:hover:not(:disabled) {
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};
        opacity: 1;
      }

      &:disabled { opacity: 0.45; cursor: not-allowed; }

      &.primary {
        background: ${tokens.brand};
        border-color: ${tokens.brand};
        color: white;
        opacity: 1;

        &:hover:not(:disabled) {
          background: ${tokens.brandHover};
          border-color: ${tokens.brandHover};
          transform: translateY(-1px);
        }
      }
    }
  }

  /* ============================================================
   * KPI CARDS — padrão Certificados (vertical, thumb sutil)
   * ============================================================ */
  .header-stats {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
    margin-bottom: 4px;
    flex-shrink: 0;

    @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 700px)  { grid-template-columns: repeat(2, 1fr); }
  }

  .stat-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    /* Override de qualquer inline style nos stat-icon — fica como thumb sutil */
    .stat-icon {
      width: auto !important;
      height: auto !important;
      background: transparent !important;
      border: none !important;
      border-radius: 0;
      padding: 0;
      opacity: 0.35;
      font-size: 0.9em;
      color: ${({ theme }) => theme.text} !important;
      display: inline-flex;
      align-items: center;
      margin-bottom: 8px;

      svg { font-size: 1em; }
    }

    > div:not(.stat-icon) {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .stat-value {
      ${mono};
      font-size: 1.6em;
      font-weight: 500;
      letter-spacing: -0.02em;
      line-height: 1.1;
      color: ${({ theme }) => theme.text};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .stat-label {
      font-size: 0.72em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      margin-top: 2px;
      letter-spacing: -0.005em;
    }

    /* Variantes coloridas — APENAS o thumb recebe cor; número fica em theme.text */
    &.success .stat-icon  { color: ${tokens.brand} !important; opacity: 0.6; }
    &.info .stat-icon     { color: #3b82f6 !important; opacity: 0.6; }
    &.cyan .stat-icon     { color: #06b6d4 !important; opacity: 0.6; }
    &.purple .stat-icon   { color: #8b5cf6 !important; opacity: 0.6; }
    &.pink .stat-icon     { color: #ec4899 !important; opacity: 0.6; }
    &.warning .stat-icon  { color: ${tokens.warn} !important; opacity: 0.6; }
    &.danger .stat-icon   { color: ${tokens.danger} !important; opacity: 0.6; }
  }

  /* ============================================================
   * TOOLBAR
   * ============================================================ */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 4px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 4px;
      flex: 1;
      min-width: 0;
      padding: 3px;
      ${({ theme }) => cardBase(theme)};
      flex-wrap: wrap;
    }

    .filter-btn {
      ${mono};
      padding: 7px 12px;
      background: transparent;
      border: none;
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      font-size: 0.7em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      &:hover { opacity: 0.9; }

      &.active {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        opacity: 1;
      }
    }

    .search-input {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 5px 10px 5px 12px;
      background: transparent;
      border: none;
      border-left: 1px solid ${({ theme }) => hairline(theme)};
      flex: 1;
      min-width: 200px;
      max-width: 320px;
      margin-left: 4px;

      svg {
        font-size: 0.85em;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        flex-shrink: 0;
      }

      input {
        flex: 1;
        background: transparent;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.78em;
        letter-spacing: -0.005em;

        &::placeholder { opacity: 0.4; }
      }

      &:focus-within {
        svg { color: ${tokens.brand}; opacity: 0.85; }
      }
    }

    .toolbar-right {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .btn-action {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: ${tokens.brand};
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      color: white;
      font-family: ${tokens.fontSans};
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      letter-spacing: -0.005em;
      white-space: nowrap;

      svg { font-size: 0.95em; }

      &:hover:not(:disabled) {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }

      &:disabled { opacity: 0.4; cursor: not-allowed; }
    }
  }

  /* ============================================================
   * LEADS LIST CONTAINER
   * ============================================================ */
  .leads-list-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    margin-top: 6px;
    padding: 4px 6px 24px 0;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 3px;
    }
  }

  .leads-count {
    ${eyebrow};
    font-size: 0.55em;
    opacity: 0.45;
    margin-bottom: 14px;
    padding-top: 4px;
  }

  .leads-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;

    @media (max-width: 1400px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 720px)  { grid-template-columns: 1fr; }
  }

  /* ============================================================
   * LEAD CARD
   * ============================================================ */
  .lead-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    ${({ theme }) => cardBase(theme)};
    cursor: pointer;
    overflow: hidden;
    min-height: 200px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      bottom: 8px;
      width: 2px;
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 0 2px 2px 0;
      opacity: 0.4;
      transition: all ${tokens.durBase} ${tokens.easeOut};
    }

    &.has-email::before {
      background: ${tokens.brand};
      opacity: 0.6;
    }

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};

      &::before { opacity: 1; }
      .card-copy { opacity: 1; }
    }

    .card-top {
      display: grid;
      grid-template-columns: 36px 1fr auto;
      gap: 10px;
      align-items: flex-start;

      .card-thumb {
        width: 36px;
        height: 36px;
        border-radius: ${tokens.radiusSm};
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        background: ${tokens.brandSoft};
        border: 1px solid ${tokens.brandRing};
        color: ${tokens.brand};
        font-family: ${tokens.fontSans};
        font-size: 0.95em;
        font-weight: 600;
        letter-spacing: -0.01em;
      }

      .card-top-right {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;

        .card-name {
          font-size: 0.88em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          line-height: 1.3;
          letter-spacing: -0.005em;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-categoria {
          ${mono};
          font-size: 0.6em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .card-rating {
        ${mono};
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 0.72em;
        font-weight: 500;
        color: ${tokens.warn};
        letter-spacing: -0.005em;
        flex-shrink: 0;
        padding-top: 2px;

        svg { font-size: 0.85em; }
      }
    }

    .card-info {
      display: flex;
      flex-direction: column;
      gap: 5px;
      flex: 1;
      padding: 6px 0 0 0;

      .info-row {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.72em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        letter-spacing: -0.005em;

        svg {
          font-size: 0.85em;
          flex-shrink: 0;
          opacity: 0.65;
        }

        span {
          ${mono};
          font-size: 0.92em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &.email {
          color: ${tokens.brand};
          opacity: 0.95;

          svg { opacity: 0.95; }
        }
      }
    }

    .card-footer {
      display: flex;
      align-items: center;
      gap: 5px;
      flex-wrap: wrap;
      margin-top: auto;
      padding-top: 10px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};

      .tag {
        ${mono};
        padding: 2px 8px;
        border-radius: ${tokens.radiusPill};
        font-size: 0.6em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        border: 1px solid;

        &.status-new {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brandRing};
        }
        &.status-contacted {
          background: rgba(59, 130, 246, 0.10);
          color: #3b82f6;
          border-color: rgba(59, 130, 246, 0.25);
        }
        &.status-qualified {
          background: rgba(168, 85, 247, 0.10);
          color: #9333ea;
          border-color: rgba(168, 85, 247, 0.25);
        }
        &.status-converted {
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brand};
        }
        &.status-discarded {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border-color: rgba(239, 68, 68, 0.22);
        }
        &.status-enriching {
          background: ${tokens.warnSoft};
          color: ${tokens.warn};
          border-color: rgba(245, 158, 11, 0.22);
        }
        &.segmento {
          background: ${({ theme }) => surface(theme, 1)};
          color: ${({ theme }) => theme.text};
          opacity: 0.65;
          border-color: ${({ theme }) => hairline(theme)};
        }
      }
    }

    .card-copy {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 28px;
      height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      cursor: pointer;
      opacity: 0;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      svg { font-size: 0.85em; }

      &:hover {
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }

      &.copied {
        background: ${tokens.brand};
        border-color: ${tokens.brand};
        color: white;
        opacity: 1;
      }
    }
  }

  /* ============================================================
   * EMPTY STATE
   * ============================================================ */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 110px 20px 80px;
    background: transparent;
    border: none;
    text-align: center;

    svg {
      width: 64px;
      height: 64px;
      padding: 18px;
      box-sizing: content-box;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: 50%;
      color: ${tokens.brand};
      opacity: 0.7;
      font-size: 1em;
      margin-bottom: 0;
    }

    p {
      margin: 0;
      font-size: 1em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    small {
      ${mono};
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
      max-width: 280px;
      line-height: 1.55;
    }
  }

  .loading-more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px 0;
    ${mono};
    font-size: 0.7em;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.text};
    opacity: 0.55;

    svg {
      font-size: 0.95em;
      animation: ${spinKf} 1s linear infinite;
      color: ${tokens.brand};
    }
  }
`;

/* ============================================================
 * DRAWER (Monitor)
 * ============================================================ */
export const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(520px, 92vw);
  background: ${({ theme }) => surface(theme, 0)};
  border-left: 1px solid ${({ theme }) => hairline(theme)};
  display: flex;
  flex-direction: column;
  z-index: 9990;
  overflow: hidden;
  box-shadow: -24px 0 60px rgba(0, 0, 0, 0.35);
  animation: ${slideInRight} ${tokens.durBase} ${tokens.easeOut};

  &.closing {
    animation: ${slideOutRight} ${tokens.durBase} ${tokens.easeOut} forwards;
  }
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  h3 {
    margin: 0;
    font-size: 0.95em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    letter-spacing: -0.01em;

    &::before {
      content: "MONITOR · TEMPO REAL";
      display: block;
      ${eyebrow};
      font-size: 0.6em;
      margin-bottom: 4px;
      opacity: 0.4;
    }
  }

  .close-btn {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    opacity: 0.65;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    flex-shrink: 0;

    svg { font-size: 0.95em; }

    &:hover {
      opacity: 1;
      border-color: ${({ theme }) => hairline(theme, true)};
    }
  }
`;

export const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

export const DrawerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .section-title {
    ${eyebrow};
    font-size: 0.58em;
    opacity: 0.55;
  }
`;

/* ============================================================
 * NODE CARD
 * ============================================================ */
export const NodeCard = styled.div`
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 6px;

  .node-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    .node-name {
      ${mono};
      font-size: 0.72em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: 0.01em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .node-status {
      ${mono};
      padding: 2px 8px;
      border-radius: ${tokens.radiusPill};
      font-size: 0.58em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      border: 1px solid transparent;
      flex-shrink: 0;

      &.idle,
      &.online,
      &.scraping {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border-color: ${tokens.brandRing};
      }

      &.busy {
        background: rgba(59, 130, 246, 0.10);
        color: #3b82f6;
        border-color: rgba(59, 130, 246, 0.25);
      }

      &.error,
      &.offline {
        background: ${tokens.dangerSoft};
        color: ${tokens.danger};
        border-color: rgba(239, 68, 68, 0.22);
      }
    }
  }

  .node-task {
    ${mono};
    font-size: 0.66em;
    color: ${({ theme }) => theme.text};
    opacity: 0.65;
    padding: 5px 8px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusSm};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    letter-spacing: 0.01em;
  }

  .node-stats {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    span {
      ${mono};
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 0.62em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      letter-spacing: 0.01em;

      svg {
        font-size: 0.85em;
        opacity: 0.7;
      }
    }
  }
`;

/* ============================================================
 * LOG TERMINAL
 * ============================================================ */
export const LogTerminal = styled.div`
  background: ${({ theme }) => surface(theme, 2)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  padding: 10px 12px;
  max-height: 280px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }

  .log-entry {
    display: grid;
    grid-template-columns: 64px 1fr;
    gap: 8px;
    align-items: baseline;
    line-height: 1.45;

    .log-time {
      ${mono};
      font-size: 0.6em;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      letter-spacing: 0.01em;
    }

    .log-msg {
      ${mono};
      font-size: 0.68em;
      letter-spacing: 0.01em;
      word-break: break-word;
      color: ${({ theme }) => theme.text};
      opacity: 0.85;

      &.error { color: ${tokens.danger}; }
      &.warn { color: ${tokens.warn}; }
      &.info { color: ${({ theme }) => theme.text}; opacity: 0.65; }
      &.success { color: ${tokens.brand}; }
      &.debug { color: ${({ theme }) => theme.text}; opacity: 0.45; }
    }
  }
`;

/* ============================================================
 * TASK QUEUE MINI
 * ============================================================ */
export const TaskQueueMini = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  .task-stat {
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    align-items: flex-start;

    .task-stat-value {
      ${mono};
      font-size: 1.05em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.02em;
      line-height: 1.1;
    }

    .task-stat-label {
      ${mono};
      font-size: 0.55em;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }
  }
`;

/* ============================================================
 * MODAL (Lead Detail)
 * ============================================================ */
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${fadeInOnly} ${tokens.durBase} ${tokens.easeOut};
`;

export const ModalContainer = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  .modal-title {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;

    svg {
      font-size: 1em;
      color: ${tokens.brand};
      flex-shrink: 0;
    }

    h2 {
      margin: 0;
      font-size: 1em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &::before {
        content: "LEAD";
        display: block;
        ${eyebrow};
        font-size: 0.55em;
        margin-bottom: 2px;
        opacity: 0.4;
      }
    }
  }

  .modal-close {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    opacity: 0.65;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    flex-shrink: 0;

    svg { font-size: 0.95em; }

    &:hover {
      opacity: 1;
      border-color: ${({ theme }) => hairline(theme, true)};
    }
  }
`;

export const ModalBody = styled.div`
  padding: 18px 20px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    overflow: hidden;

    @media (max-width: 600px) { grid-template-columns: 1fr; }
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 14px;
    background: ${({ theme }) => surface(theme, 0)};
    border-right: 1px solid ${({ theme }) => hairline(theme)};
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};

    &:nth-child(2n) { border-right: none; }

    @media (max-width: 600px) {
      border-right: none;
    }

    &.full-width {
      grid-column: 1 / -1;
      border-right: none;
    }

    .detail-label {
      ${mono};
      font-size: 0.6em;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    .detail-value {
      ${mono};
      font-size: 0.78em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.005em;
      word-break: break-word;

      a {
        color: ${tokens.brand};
        text-decoration: none;

        &:hover { text-decoration: underline; }
      }
    }
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px 16px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  flex-shrink: 0;
  flex-wrap: wrap;

  .modal-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: transparent;
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.85;
    font-family: ${tokens.fontSans};
    font-size: 0.78em;
    font-weight: 500;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    letter-spacing: -0.005em;
    white-space: nowrap;

    &:hover {
      background: ${({ theme }) => surface(theme, 1)};
      border-color: ${({ theme }) => hairline(theme, true)};
      opacity: 1;
    }

    &.primary {
      background: ${tokens.brand};
      color: white;
      border-color: ${tokens.brand};
      opacity: 1;

      &:hover {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }
    }
  }
`;

/* ============================================================
 * SKELETON
 * ============================================================ */
export const SkeletonPulse = styled.div`
  width: 100%;
  height: ${({ $height }) => $height || '80px'};
  border-radius: ${({ $radius }) => $radius || tokens.radiusLg};
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  animation: ${skeletonPulse} 1.5s ${tokens.easeInOut} infinite;
`;
